// Authentication provider.
//
// Holds a real AWS Cognito session (SRP, via amazon-cognito-identity-js) and exposes the
// id token to the API client. On mount it restores any existing session from the
// refresh token so a page reload stays signed in. Invite-only pools issue a temporary
// password; the first sign-in surfaces a NEW_PASSWORD_REQUIRED challenge that the Login
// page completes via `pendingNewPassword`.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import {
  AuthContext,
  type AuthState,
  type PendingNewPassword,
  type PendingTotp,
  type PendingTotpSetup,
} from "./auth-context";
import {
  completeNewPassword,
  currentSession,
  idToken as sessionIdToken,
  NewPasswordRequiredError,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  submitTotpCode,
  TotpRequiredError,
  TotpSetupRequiredError,
  verifyTotpSetup,
  type CognitoUserSession,
} from "./cognito";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingNewPassword, setPendingNewPassword] = useState<PendingNewPassword | null>(null);
  const [pendingTotp, setPendingTotp] = useState<PendingTotp | null>(null);
  const [pendingTotpSetup, setPendingTotpSetup] = useState<PendingTotpSetup | null>(null);
  // Guards against setting state after unmount during the async restore.
  const mounted = useRef(true);

  const applySession = useCallback((session: CognitoUserSession) => {
    setIdToken(sessionIdToken(session));
    setPendingNewPassword(null);
    setPendingTotp(null);
    setPendingTotpSetup(null);
  }, []);

  useEffect(() => {
    mounted.current = true;
    void (async () => {
      try {
        const session = await currentSession();
        if (mounted.current && session) {
          setIdToken(sessionIdToken(session));
        }
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
    return () => {
      mounted.current = false;
    };
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      try {
        const session = await cognitoSignIn(email, password);
        applySession(session);
      } catch (err) {
        if (err instanceof NewPasswordRequiredError) {
          const { user, userAttributes } = err;
          setPendingNewPassword({
            complete: async (newPassword: string) => {
              const session = await completeNewPassword(user, newPassword, userAttributes);
              applySession(session);
            },
          });
          return;
        }
        if (err instanceof TotpRequiredError) {
          const { user } = err;
          setPendingTotp({
            submit: async (code: string) => {
              const session = await submitTotpCode(user, code);
              applySession(session);
            },
          });
          return;
        }
        if (err instanceof TotpSetupRequiredError) {
          const { user, secretCode, otpauthUri } = err;
          setPendingTotpSetup({
            secretCode,
            otpauthUri,
            verify: async (code: string) => {
              const session = await verifyTotpSetup(user, code);
              applySession(session);
            },
          });
          return;
        }
        throw err;
      }
    },
    [applySession],
  );

  const signOut = useCallback(() => {
    cognitoSignOut();
    setIdToken(null);
    setPendingNewPassword(null);
    setPendingTotp(null);
    setPendingTotpSetup(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      isAuthenticated: Boolean(idToken),
      loading,
      idToken,
      pendingNewPassword,
      pendingTotp,
      pendingTotpSetup,
      signIn,
      signOut,
    }),
    [idToken, loading, pendingNewPassword, pendingTotp, pendingTotpSetup, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
